//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import { useDispatch } from 'react-redux';

import { useCreateClientFlagsMutation } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { BodyText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import makeAngularReactControllerWithScope from '@react-bridge/angular-react-widget-with-scope';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';

import { ISelectedItem } from 'app/bulk-actions/model/ISelectedItem';

interface UiaReportAiContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReportError?: () => void;
  onReportSuccess?: () => void;
  selectedItems: ISelectedItem[];
  zIndexOverlayOverride?: string;
}

export const UiaReportAiContentModal = ({
  isOpen,
  onClose,
  onReportError,
  onReportSuccess,
  selectedItems,
  zIndexOverlayOverride,
}: UiaReportAiContentModalProps) => {
  const dispatch = useDispatch();
  const { isPending, mutate: createClientFlags } =
    useCreateClientFlagsMutation();

  const onConfirm = () => {
    createClientFlags(selectedItems, {
      onSuccess: () => {
        dispatch(getBulkActionsActions().clearSelectedItems());

        if (onReportSuccess) {
          onReportSuccess();
        } else {
          dispatch(
            toastNotificationsActions.addToastNotification({
              title: 'Report Submitted.',
              message:
                'The AI content report has been submitted to our moderation team.',
              type: ToastType.SUCCESS,
              options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
            }),
          );
        }
      },
      onError: () => {
        if (onReportError) {
          onReportError();
        } else {
          dispatch(
            toastNotificationsActions.addToastNotification({
              title: "Couldn't Report Content",
              message:
                'Something went wrong while reporting the content. Please try again.',
              type: ToastType.ERROR,
              options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
            }),
          );
        }
      },
      onSettled: () => onClose(),
    });
  };

  return (
    <Modal
      closeModal={onClose}
      contents={
        <div>
          <BodyText>
            Reporting this content will send it to our moderation team.
          </BodyText>
          <br />
          <BodyText>
            If our moderator agrees that the content is AI-generated, it will be
            automatically removed and the member will be notified that they need
            to resubmit the content.
          </BodyText>
        </div>
      }
      isModalOpen={isOpen}
      primaryButtonInfo={{
        buttonContent: 'Report Content',
        isDisabled: isPending,
        onClick: onConfirm,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REPORT_AI_CONTENT_CONFIRM_BUTTON,
      }}
      secondaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: onClose,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REPORT_AI_CONTENT_CANCEL_BUTTON,
      }}
      title='Report AI Content'
      zIndexOverlayOverride={zIndexOverlayOverride}
    />
  );
};

angular.module('curalate').component('uiaReportAiContentModal', {
  bindings: {
    isOpen: '<',
    onClose: '&',
    selectedItems: '<',
  },
  controller: makeAngularReactControllerWithScope(
    ({ $ctrl }: { $ctrl: UiaReportAiContentModalProps }) => {
      return <UiaReportAiContentModal {...$ctrl} />;
    },
  ),
});
